import { notification } from 'antd';
import jwtDecode from 'jwt-decode';
import { useCallback, useEffect, useRef } from 'react';

import { google } from '../config/externalServices';

export const GOOGLE_REG_CONFIRM_SSO_ERROR_NOTIFICATION_KEY = 'google-reg-confirm-sso-error';

const GOOGLE_IDENTITY_SCRIPT_ID = 'google-identity-services';
const GOOGLE_IDENTITY_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

let googleScriptPromise;

const loadGoogleScript = () => {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }

  if (googleScriptPromise) {
    return googleScriptPromise;
  }

  googleScriptPromise = new Promise((resolve, reject) => {
    const existingScript =
      document.getElementById(GOOGLE_IDENTITY_SCRIPT_ID) ||
      document.querySelector(`script[src^="${GOOGLE_IDENTITY_SCRIPT_SRC}"]`);

    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_IDENTITY_SCRIPT_ID;
    script.src = GOOGLE_IDENTITY_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return googleScriptPromise;
};

const useGoogleRegConfirmSso = ({ enabled, onCredential, onError }) => {
  const buttonContainerRef = useRef(null);
  const googleButtonRef = useRef(null);
  const onCredentialRef = useRef(onCredential);
  const onErrorRef = useRef(onError);

  onCredentialRef.current = onCredential;
  onErrorRef.current = onError;

  const reportError = useCallback((message) => {
    notification.error({
      key: GOOGLE_REG_CONFIRM_SSO_ERROR_NOTIFICATION_KEY,
      message
    });
    onErrorRef.current?.();
  }, []);

  const handleCredential = useCallback(
    ({ credential }) => {
      try {
        const email = jwtDecode(credential)?.email;

        if (!email) {
          reportError('Google did not return an email address. Please try another account.');
          return;
        }

        onCredentialRef.current?.(email);
      } catch {
        reportError('Google sign in failed. Please try again.');
      }
    },
    [reportError]
  );

  useEffect(() => {
    if (!enabled) {
      googleButtonRef.current = null;
      return undefined;
    }

    let isMounted = true;

    loadGoogleScript()
      .then(() => {
        const googleIdentity = window.google?.accounts?.id;

        if (!isMounted || !buttonContainerRef.current || !googleIdentity) {
          return;
        }

        googleIdentity.initialize({
          client_id: google.oauthClientId,
          callback: handleCredential,
          cancel_on_tap_outside: false
        });

        buttonContainerRef.current.replaceChildren();
        googleIdentity.renderButton(buttonContainerRef.current, {
          theme: 'outline',
          size: 'large'
        });
        googleButtonRef.current = buttonContainerRef.current.querySelector('[role="button"]');
      })
      .catch(() => {
        googleScriptPromise = undefined;

        if (isMounted) {
          reportError('Google sign in is unavailable. Please try again later.');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [enabled, handleCredential, reportError]);

  const trigger = useCallback(() => {
    const googleButton =
      googleButtonRef.current || buttonContainerRef.current?.querySelector('[role="button"]');

    if (!googleButton) {
      reportError('Google sign in is unavailable. Please try again later.');
      return false;
    }

    googleButton.click();
    return true;
  }, [reportError]);

  return {
    buttonContainerRef,
    trigger
  };
};

export default useGoogleRegConfirmSso;
